import {InvoiceRowForm} from './_models'
import {InvoiceRowPolicyV79} from './invoiceRulesV79'

export const TEMPLATE_14_SINGLE_ITEM_ERROR =
  'الگوی ۱۴ صورتحساب فقط می‌تواند یک ردیف کالا/خدمت داشته باشد.'

export const ZERO_VAT_INVOICE_TEMPLATES = [7, 11, 14]
export const THIRD_PARTY_CONTRACT_NUMBER_LENGTH = 12

export const isZeroVatInvoiceTemplate = (invoiceTemplate: number) =>
  ZERO_VAT_INVOICE_TEMPLATES.includes(invoiceTemplate)

export const normalizeThirdPartyContractNumberInput = (value: unknown) =>
  String(value ?? '')
    .trim()
    .replace(/[۰-۹]/g, (digit) => String(digit.charCodeAt(0) - '۰'.charCodeAt(0)))
    .replace(/[٠-٩]/g, (digit) => String(digit.charCodeAt(0) - '٠'.charCodeAt(0)))

export const getThirdPartyContractNumberValidationError = (value: unknown) => {
  if (value === null || value === undefined || value === '' || value === 0) return ''

  const normalized = normalizeThirdPartyContractNumberInput(value)
  if (!/^\d{12}$/.test(normalized)) {
    return 'شماره قرارداد معامله از طریق شخص ثالث باید دقیقاً ۱۲ رقم باشد.'
  }

  return ''
}

// did: tp.tax.gov.ir/docs 2.0, invoice field table, field 095. The server (MegaPay.FiscalMemory
// InvoiceIdentifierValidator) is the authority; this is the form's early feedback.
export const getDriverIdCodeValidationError = (value: unknown) => {
  const normalized = normalizeThirdPartyContractNumberInput(value)
  if (normalized === '' || /^(\d{10}|\d{12})$/.test(normalized)) return ''

  return 'شماره ملی راننده ۱۰ رقم و کد فراگیر اتباع غیرایرانی ۱۲ رقم است؛ شناسه ملی ۱۱ رقمی اشخاص حقوقی پذیرفته نمی‌شود.'
}

// cfee: the same table, field 038 (server: InvoiceCurrencyAmountPolicy). A value that is not a
// number is left to the form's other checks.
export const CURRENCY_AMOUNT_MAX_INTEGER_DIGITS = 14
export const CURRENCY_AMOUNT_MAX_DECIMALS = 4

export const getCurrencyAmountValidationError = (value: unknown) => {
  const match = /^(\d*)(?:\.(\d*))?$/.exec(normalizeThirdPartyContractNumberInput(value))
  if (!match) return ''

  const integerDigits = match[1].replace(/^0+/, '')
  const decimals = (match[2] ?? '').replace(/0+$/, '')
  if (
    integerDigits.length <= CURRENCY_AMOUNT_MAX_INTEGER_DIGITS &&
    decimals.length <= CURRENCY_AMOUNT_MAX_DECIMALS
  ) {
    return ''
  }

  return 'میزان ارز حداکثر ۱۴ رقم صحیح و ۴ رقم اعشار دارد.'
}

const finiteNumber = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }

  const number = Number(value)
  return Number.isFinite(number) ? number : undefined
}

export const hasInvoiceItemVatRateOverride = (items: InvoiceRowForm[] = []) =>
  items.some((item) => {
    const enteredVatRate = finiteNumber(item.vatRate)
    const referenceVatRate = finiteNumber(item.product?.vtr)

    return (
      enteredVatRate !== undefined &&
      referenceVatRate !== undefined &&
      enteredVatRate !== referenceVatRate
    )
  })

const numberOrZero = (value?: number) => (Number.isFinite(value) ? value! : 0)

export const normalizeInvoicePatternItems = (
  invoiceTemplate: number,
  items: InvoiceRowForm[] = []
) => {
  if (invoiceTemplate != 3) {
    return items
  }

  return items.map((item) => ({
    ...item,
    wage: numberOrZero(item.wage),
    profit: numberOrZero(item.profit),
    commission: numberOrZero(item.commission),
  }))
}

export const getInvoicePatternValidationError = (
  invoiceTemplate: number,
  items: InvoiceRowForm[] = [],
  rowPolicy?: InvoiceRowPolicyV79
) => {
  const invalidContractNumberIndex =
    invoiceTemplate === 11
      ? -1
      : items.findIndex(
          (item) => getThirdPartyContractNumberValidationError(item.contractNo) !== ''
        )
  if (invalidContractNumberIndex >= 0) {
    return `شماره قرارداد معامله از طریق شخص ثالث در ردیف ${
      invalidContractNumberIndex + 1
    } باید دقیقاً ۱۲ رقم باشد.`
  }

  if (rowPolicy) {
    if (!rowPolicy.bodyAllowed && items.length > 0) return 'بدنه برای این موضوع صورتحساب خارج از الگو است.'
    if (items.length < rowPolicy.minimumRows)
      return `حداقل ${rowPolicy.minimumRows} ردیف کالا/خدمت الزامی است.`
    if (rowPolicy.maximumRows && items.length > rowPolicy.maximumRows)
      return rowPolicy.message || `حداکثر ${rowPolicy.maximumRows} ردیف کالا/خدمت مجاز است.`
    if (rowPolicy.amountMustEqualOne && items.some((item) => Number(item.qty) !== 1))
      return 'طبق قواعد V7.9 مقدار am برای هر ردیف این الگو باید ۱ باشد.'
  } else if ((invoiceTemplate == 11 || invoiceTemplate == 14) && items.length > 1) {
    return invoiceTemplate == 14
      ? TEMPLATE_14_SINGLE_ITEM_ERROR
      : 'الگوی ۱۱ بورس اوراق بهادار مبتنی بر کالا فقط می‌تواند یک ردیف کالا/خدمت داشته باشد.'
  }

  if ((invoiceTemplate == 6 || invoiceTemplate == 8) && items.some((item) => Number(item.qty) !== 1)) {
    return 'در الگوی بلیط هواپیما و بارنامه مقدار هر ردیف باید ۱ باشد.'
  }

  if (invoiceTemplate == 2) {
    const invalidItem = items.find(
      (item) =>
        !(item.amountCurrency && item.amountCurrency > 0) ||
        !item.currency ||
        !(item.currencyRate && item.currencyRate > 0) ||
        !(item.currencyPurchaseRate && item.currencyPurchaseRate > 0) ||
        item.currencyPurchaseRate >= item.currencyRate ||
        item.vatBaseAmount == undefined
    )
    if (invalidItem) {
      return 'در الگوی فروش ارز، میزان ارز، نوع ارز، نرخ فروش، نرخ خرید و مأخذ مالیات بر ارزش افزوده برای همه ردیف‌ها الزامی است.'
    }
  }

  if (invoiceTemplate == 3) {
    const normalizedItems = normalizeInvoicePatternItems(invoiceTemplate, items)
    const invalidGoldAmount = normalizedItems.find(
      (item) => item.wage! < 0 || item.profit! < 0 || item.commission! < 0
    )
    if (invalidGoldAmount) {
      return 'در الگوی طلا، اجرت ساخت، سود فروشنده و حق‌العمل نباید منفی باشد.'
    }

    const invalidCaratIndex = normalizedItems.findIndex(
      (item) => item.carat != undefined && (item.carat <= 0 || item.carat > 1000)
    )
    if (invalidCaratIndex >= 0) {
      return `در الگوی طلا، عیار ردیف ${
        invalidCaratIndex + 1
      } باید بزرگ‌تر از صفر و حداکثر ۱۰۰۰ باشد.`
    }
  }

  if (invoiceTemplate == 7) {
    const invalidItem = items.find(
      (item) =>
        !(item.amount && item.amount > 0) ||
        !(item.currencyValue && item.currencyValue > 0) ||
        !item.currency ||
        !(item.currencyRate && item.currencyRate > 0)
    )
    if (invalidItem) {
      return 'در الگوی صادرات، ارزش ریالی، ارزش ارزی، نوع ارز و نرخ برابری ارز برای همه ردیف‌ها الزامی است.'
    }
  }

  if (invoiceTemplate == 9) {
    const invalidItem = items.find(
      (item) => item.vatBaseAmount != undefined && item.vatBaseAmount < 0
    )
    if (invalidItem) {
      return 'در الگوی فروش فرآورده‌های نفتی، مبلغ پایه مالیات بر ارزش افزوده نباید منفی باشد.'
    }
  }

  return ''
}
